import { useState } from "react";
import { useUsers, useUserAuditTrail, type AdminUserRow, type UserAuditTrailRow } from "@/lib/queries";
import {
  useDeleteUser, useResetUserPassword, useToggleUserActive, useForceLogoutUser,
} from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EllipsisVertical, UserCog, Plus, KeyRound, Power, LogOut, ScrollText, Trash, Pencil, Copy, RotateCcwClock } from "lucide-react";
import { fmtDateTime, fmtDate } from "@emr/shared/india";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CreateUserDialog } from "@/features/admin/CreateUserDialog";
import { EditUserDialog } from "@/features/admin/EditUserDialog";
import { PermissionTemplateManager } from "@/features/admin/PermissionTemplateManager";

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const { data, isLoading } = useUsers();
  const [createOpen, setCreateOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUserRow | null>(null);

  const users = data?.users ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Users & Permissions</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTemplatesOpen(true)}>
            <UserCog size={14} className="mr-1" /> Manage Templates
          </Button>
          <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => setCreateOpen(true)}>
            <Plus size={14} className="mr-1" /> Add Person
          </Button>
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Perms</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-12">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7}><Skeleton className="h-10" /></TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted py-8">No users</TableCell></TableRow>
              ) : users.map((u) => (
                <UserRow key={u.id} u={u} isSelf={u.id === currentUser?.id} onEdit={() => setEditingUser(u)} />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
      <PermissionTemplateManager open={templatesOpen} onOpenChange={setTemplatesOpen} />
      <EditUserDialog
        open={!!editingUser}
        onOpenChange={(v) => { if (!v) setEditingUser(null); }}
        user={editingUser}
        isSelf={editingUser?.id === currentUser?.id}
      />
    </div>
  );
}

function UserRow({ u, isSelf, onEdit }: { u: AdminUserRow; isSelf: boolean; onEdit: () => void }) {
  const del = useDeleteUser();
  const reset = useResetUserPassword();
  const toggle = useToggleUserActive(u.id);
  const forceLogout = useForceLogoutUser(u.id);
  const [resetOpen, setResetOpen] = useState(false);
  const [tempPw, setTempPw] = useState<string | null>(null);
  const [trailOpen, setTrailOpen] = useState(false);

  const doReset = async (pw?: string) => {
    try {
      const r = await reset.mutateAsync({ id: u.id, password: pw });
      setTempPw(r.tempPassword);
    } catch { /* handled */ }
  };

  const doDelete = async () => {
    if (!confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try { await del.mutateAsync(u.id); } catch { /* handled */ }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">
        <button className="text-left hover:underline text-maroon-700" onClick={onEdit}>{u.name}</button>
        {isSelf && <Badge variant="outline" className="ml-2 text-[10px]">you</Badge>}
      </TableCell>
      <TableCell className="text-sm">{u.email}</TableCell>
      <TableCell>
        {u.role === "admin" ? <Badge className="bg-maroon-700 text-cream-50">admin</Badge> : <Badge variant="outline">user</Badge>}
      </TableCell>
      <TableCell className="text-sm">
        {u.role === "admin" ? <span className="text-muted">all</span> : `${u.permissionCount} / ${u.permissionTotal}`}
      </TableCell>
      <TableCell>
        {u.status === "Active" ? <Badge className="bg-green-700 text-cream-50">active</Badge> : <Badge variant="secondary">inactive</Badge>}
      </TableCell>
      <TableCell className="text-xs text-muted">{u.createdAt ? fmtDate(u.createdAt) : "—"}</TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8"><EllipsisVertical size={14} /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={onEdit}><Pencil size={12} className="mr-2" /> Edit</DropdownMenuItem>
            {!isSelf && (
              <>
                <DropdownMenuItem onClick={() => toggle.mutate()}>
                  <Power size={12} className="mr-2" /> {u.status === "Active" ? "Deactivate" : "Activate"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => forceLogout.mutate()}>
                  <LogOut size={12} className="mr-2" /> Force logout
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setResetOpen(true)}>
                  <KeyRound size={12} className="mr-2" /> Reset password
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem onClick={() => setTrailOpen(true)}>
              <RotateCcwClock size={12} className="mr-2" /> View audit trail
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* B8-1 (2026-11-01): ?user= filters audit_log.changed_by, which
                stores the Postgres profiles.id UUID — pass that, not the
                Appwrite $id (fallback keeps old behavior for profile-less
                drift users, who can have no audit rows anyway). */}
            <Link to={`/admin/audit-log?user=${u.profileId ?? u.id}`}>
              <DropdownMenuItem><ScrollText size={12} className="mr-2" /> View all actions</DropdownMenuItem>
            </Link>
            {!isSelf && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={doDelete}>
                  <Trash size={12} className="mr-2" /> Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>

      <ResetPasswordDialog open={resetOpen} onOpenChange={setResetOpen} onReset={doReset} tempPw={tempPw} onCloseTempPw={() => setTempPw(null)} />
      <AuditTrailPopover open={trailOpen} onOpenChange={setTrailOpen} userId={u.id} profileId={u.profileId} />
    </TableRow>
  );
}

function ResetPasswordDialog({
  open, onOpenChange, onReset, tempPw, onCloseTempPw,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onReset: (pw?: string) => Promise<void>;
  tempPw: string | null;
  onCloseTempPw: () => void;
}) {
  const [password, setPassword] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>Set a new password for this user. They will be signed out and must change it on next login.</DialogDescription>
        </DialogHeader>
        {tempPw ? (
          <div className="space-y-3">
            <Alert>Temporary password generated. Show this once to the user.</Alert>
            <div className="flex items-center gap-2">
              <Input readOnly value={tempPw} className="font-mono" />
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(tempPw); toast.success("Copied"); }}>
                <Copy size={14} />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label>New password (leave blank to auto-generate)</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Auto-generate" />
          </div>
        )}
        <DialogFooter>
          {tempPw ? (
            <Button onClick={() => { onCloseTempPw(); onOpenChange(false); setPassword(""); }}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => onReset(password || undefined)}>
                <KeyRound size={14} className="mr-1" /> Reset
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditTrailPopover({ open, onOpenChange, userId, profileId }: { open: boolean; onOpenChange: (v: boolean) => void; userId: string; profileId: string | null }) {
  const { data, isLoading } = useUserAuditTrail(userId);
  const rows = (data?.rows ?? []).slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Recent actions</DialogTitle>
          <DialogDescription>Last 5 actions by this user.</DialogDescription>
        </DialogHeader>
        {isLoading ? <Skeleton className="h-24" /> : rows.length === 0 ? (
          <p className="text-sm text-muted text-center py-6">No actions recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r: UserAuditTrailRow) => (
              <div key={r.id} className="rounded-md border border-cream-200 bg-cream-50 p-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">{r.action}</span>
                  <span className="text-xs text-muted">{fmtDateTime(r.changedAt)}</span>
                </div>
                <div className="text-xs text-muted">{r.tableName}{r.recordId ? ` · ${r.recordId.slice(0, 8)}` : ""}</div>
              </div>
            ))}
            {/* B8-1 (2026-11-01): ?user= filters audit_log.changed_by, which
                stores the Postgres profiles.id UUID. `userId` here is the
                Appwrite $id (what the audit-trail route expects) — use the
                profile UUID for the link, not the Appwrite id. */}
            <Link to={`/admin/audit-log?user=${profileId ?? userId}`} className="block text-center text-sm text-maroon-700 hover:underline mt-3">
              View all actions →
            </Link>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
